import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { provinces, houseTypes, ownershipStatus } from '../mockData';
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getLeadTrackingFields } from '../utils/leadTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadFormPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    province: '',
    houseType: '',
    ownershipStatus: '',
    problemDescription: '',
    website: ''
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/leads`, { ...formData, ...getLeadTrackingFields(), tipoIntervento: 'Richiesta sopralluogo' });

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17960264240/HNy_CMqrr_obELDEj_RC',
          'value': 1.0,
          'currency': 'EUR'
        });
      }
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', {
          content_name: 'Sopralluogo Gratuito',
          content_category: 'Lead Form',
          value: 1.0,
          currency: 'EUR'
        });
      }

      setFormData({ name: '', email: '', phone: '', province: '', houseType: '', ownershipStatus: '', problemDescription: '' });
      setStep(1);
      onClose();
      navigate('/grazie');
    } catch (error) {
      toast.error("Errore nell'invio", {
        description: error.response?.data?.detail || "Si e verificato un errore. Riprova piu tardi.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedStep1 = formData.name && formData.phone && formData.email;
  const canProceedStep2 = formData.province && formData.houseType;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white border-slate-200 text-slate-900" data-testid="lead-form-popup">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            {step === 1 && "Richiedi Sopralluogo Gratuito"}
            {step === 2 && "Dettagli della tua abitazione"}
            {step === 3 && "Descrivi il problema"}
          </DialogTitle>
        </DialogHeader>

        {/* Progress */}
        <div className="flex gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-all ${
                s <= step ? 'bg-[#FF5722]' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <input type="text" name="website" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-slate-700 text-sm">Nome e Cognome *</Label>
                <Input
                  id="name"
                  data-testid="popup-form-name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Es. Mario Rossi"
                  required
                  className="mt-1 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722]"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700 text-sm">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="popup-form-email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  placeholder="esempio@email.com"
                  required
                  className="mt-1 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722]"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-700 text-sm">Telefono *</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="popup-form-phone"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  placeholder="+39 123 456 7890"
                  required
                  className="mt-1 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722]"
                />
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                <p className="text-xs text-slate-600">
                  I tuoi dati sono al sicuro e verranno utilizzati solo per contattarti riguardo al sopralluogo.
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700 text-sm">Provincia *</Label>
                <Select value={formData.province} onValueChange={(value) => updateField('province', value)}>
                  <SelectTrigger className="mt-1 bg-white border-slate-200 text-slate-900">
                    <SelectValue placeholder="Seleziona la tua provincia" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {provinces.map((province) => (
                      <SelectItem key={province} value={province}>{province}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 text-sm">Tipologia abitazione *</Label>
                <Select value={formData.houseType} onValueChange={(value) => updateField('houseType', value)}>
                  <SelectTrigger className="mt-1 bg-white border-slate-200 text-slate-900">
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    {houseTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 text-sm">Sei proprietario?</Label>
                <Select value={formData.ownershipStatus} onValueChange={(value) => updateField('ownershipStatus', value)}>
                  <SelectTrigger className="mt-1 bg-white border-slate-200 text-slate-900">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    {ownershipStatus.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700 text-sm">Descrivi il problema del tuo tetto</Label>
                <Textarea
                  data-testid="popup-form-description"
                  value={formData.problemDescription}
                  onChange={(e) => updateField('problemDescription', e.target.value)}
                  placeholder="Es. Perdite d'acqua, tegole rotte, necessita di sostituzione eternit..."
                  rows={5}
                  className="mt-1 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722]"
                />
              </div>
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-3 text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Cosa succede dopo?
                </h4>
                <ul className="space-y-1.5 text-xs text-slate-600">
                  <li>Ti contatteremo entro 24 ore</li>
                  <li>Fissiamo un appuntamento per il sopralluogo gratuito</li>
                  <li>Ricevi un preventivo dettagliato senza impegno</li>
                </ul>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(step - 1)}
                className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
            )}
            {step < 3 ? (
              <Button
                type="button"
                onClick={() => setStep(step + 1)}
                disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
                className="flex-1 bg-[#FF5722] hover:bg-[#FF7043] text-white"
              >
                Avanti
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="popup-form-submit"
                className="flex-1 bg-[#FF5722] hover:bg-[#FF7043] text-white"
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta'}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormPopup;
